import { useState } from 'react'
import { Outlet } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { FeatureWall } from 'modules/core/components/FeatureWall'
import { default as ToggleSelector } from 'modules/core/components/ToggleSelector'
import { useEffect } from 'modules/core/hooks/useEffect'
import { FormProvider } from 'modules/core/hooks/useForm'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useLocations } from 'modules/locations/hooks/useLocations'
import { useWorkAreas } from 'modules/locations/hooks/useWorkAreas'
import BookingLimits from 'modules/space_control/components/BookingLimits'
import WorkSpaceFilter from 'modules/space_control/components/WorkSpaceFilter'
import WorkSpacesList from 'modules/space_control/components/WorkSpacesList'
import LoadingWorkSpaces from 'modules/space_control/components/WorkSpacesList/LoadingWorkSpaces'
import { WorkSpacesMap } from 'modules/space_control/components/WorkSpacesMap'
import LoadingMap from 'modules/space_control/components/WorkSpacesMap/LoadingState'
import { DateBookingProvider } from 'modules/space_control/components/dateBookingContext'
import { useBookingLimits } from 'modules/space_control/hooks/useBookingLimits'
import {
  WorkSpaceWithBookingNode,
  useWorkSpacesWithBookings,
} from 'modules/space_control/hooks/useWorkSpacesWithBookings'
import { useUserMapEvents } from 'modules/space_control/hooks/workSpacesMap/useMapEvents'

const MODE = {
  CARDS: 'cards',
  MAP: 'map',
}

interface CardsViewProps {
  loading: boolean
  workSpaces: WorkSpaceWithBookingNode[]
}

const CardsView = ({ loading, workSpaces }: CardsViewProps) => {
  if (loading) return <LoadingWorkSpaces />
  return <WorkSpacesList workSpaces={workSpaces} />
}

interface MapViewProps {
  loading: boolean
  workSpaces: WorkSpaceWithBookingNode[]
  workAreaId: string
}

const MapView = ({ loading, workAreaId, workSpaces }: MapViewProps) => {
  const { actions } = useUserMapEvents({
    workSpaces,
  })

  if (loading) return <LoadingMap />

  return <WorkSpacesMap workAreaId={workAreaId} workSpaces={workSpaces} actions={actions} />
}

const OverviewPage = () => {
  const { locations } = useLocations()
  const { workAreas } = useWorkAreas({
    locationIds: locations.map((location) => location.id),
    onlyNonArchived: true,
  })
  const [viewMode, setViewMode] = useState(MODE.CARDS)

  useEffect(() => {
    track(trackingEvents.spaceControl.seeOverview)
  }, [])

  const defaultValues = {
    startTime: '',
    endTime: '',
    locationId: '',
    workAreaId: '',
    date: i18nDateToString(moment(), 'backend'),
  }

  const [variables, setVariables] = useState<{
    startTime: string
    endTime: string
    locationId: string
    workAreaId: string
    date: string
  }>(defaultValues)
  const canSeeMap = variables.workAreaId && variables.workAreaId !== ''

  useEffect(() => {
    if (!canSeeMap) setViewMode(MODE.CARDS)
  }, [variables.workAreaId, canSeeMap])

  const { workSpaces, loading } = useWorkSpacesWithBookings({
    startTime: variables.startTime,
    endTime: variables.endTime,
    date: i18nDateToString(moment(variables.date), 'backend'),
    locationIds: variables.locationId ? [Number(variables.locationId)] : [],
    workAreaIds: variables.workAreaId ? [Number(variables.workAreaId)] : [],
  })

  const { loading: loadingBookingAvailabilities, bookingAvailability } = useBookingLimits()
  const bookingsLimitBanner = <BookingLimits bookingAvailability={bookingAvailability} />

  const notFullWorkspaces = useUnsafeMemo(() => {
    const spaces = [...workSpaces]
    let res
    if (
      variables.startTime.length !== 0 ||
      variables.endTime.length !== 0 ||
      variables.locationId.length !== 0
    ) {
      return spaces.filter((workspace) => !workspace.isFull)
    } else {
      res = spaces.sort((a, b) => Number(a.isFull) - Number(b.isFull))
      return res
    }
  }, [variables.startTime, variables.endTime, variables.locationId, workSpaces])

  const handleViewModeChange = (value: string) => {
    if (!canSeeMap) return
    setViewMode(value)
  }

  return (
    <ResponsivePage.Body showHeader={false}>
      {!loadingBookingAvailabilities && bookingsLimitBanner}
      <FormProvider
        defaultValues={defaultValues}
        onSubmit={(values) => {
          setVariables({
            ...(values.locationId ? { locationId: values.locationId } : { locationId: '' }),
            ...(values.startTime ? { startTime: values.startTime } : { startTime: '' }),
            ...(values.endTime ? { endTime: values.endTime } : { endTime: '' }),
            ...(values.workAreaId ? { workAreaId: values.workAreaId } : { workAreaId: '' }),
            date: values.date,
          })
          return Promise.resolve({ success: true, data: '' })
        }}
      >
        <DateBookingProvider value={{ date: moment(variables.date) }}>
          <Box flexGrow flexDirection="column" gap="s24">
            <Box flexDirection="row" gap="s24" alignItems="center" justifyContent="spaceBetween">
              <Text.Normal weight="medium">
                {i18n.tp('space_control.overview.spaces_count', {
                  count: workSpaces.length,
                })}
              </Text.Normal>

              <Box flexDirection="row" gap="s24" alignItems="center">
                <FeatureWall featureName="dev_space_control_map_work_areas">
                  {canSeeMap && (
                    <ToggleSelector
                      value={viewMode}
                      options={[
                        {
                          icon: 'more-options',
                          label: i18n.t('space_control.overview.toggle.cards'),
                          value: MODE.CARDS,
                        },
                        {
                          icon: 'pin',
                          label: canSeeMap
                            ? i18n.t('space_control.overview.toggle.map')
                            : i18n.t('space_control.overview.toggle.map_disabled'),
                          value: MODE.MAP,
                        },
                      ]}
                      onChange={handleViewModeChange}
                    />
                  )}
                </FeatureWall>
              </Box>
            </Box>

            <Box flexGrow flexDirection="row" gap="s24" alignItems="stretch">
              <Box>
                <WorkSpaceFilter locations={locations} workAreas={workAreas} />
              </Box>
              {viewMode === MODE.CARDS ? (
                <CardsView loading={loading} workSpaces={notFullWorkspaces} />
              ) : (
                <MapView
                  loading={loading}
                  workSpaces={notFullWorkspaces}
                  workAreaId={variables.workAreaId}
                />
              )}
            </Box>
          </Box>
        </DateBookingProvider>
      </FormProvider>
      <Outlet />
    </ResponsivePage.Body>
  )
}

export default OverviewPage
